import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinanceStore } from '../../store/useFinanceStore';
import { isCountableTransaction } from '../../utils/transactionMetrics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DEFAULT_TITLE = 'Fluxo de Caixa (Últimos 3 meses)';

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTH_NAMES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface BarChartProps {
  title?: string;
}

const BarChart: React.FC<BarChartProps> = ({ title = DEFAULT_TITLE }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { transactions } = useFinanceStore();
  const [offsetMonths, setOffsetMonths] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    setAnimationProgress(0);

    const interval = setInterval(() => {
      setAnimationProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 5;
      });
    }, 30);

    return () => clearInterval(interval);
  }, [offsetMonths]);

  const months = useMemo(() => {
    const now = new Date();
    const result: Array<{ label: string; fullLabel: string; income: number; expenses: number }> = [];

    for (let i = 2; i >= 0; i -= 1) {
      const monthOffset = i + offsetMonths;
      const targetDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
      const month = targetDate.getMonth();
      const year = targetDate.getFullYear();

      const monthTransactions = transactions.filter((transaction) => {
        if (!isCountableTransaction(transaction, now)) {
          return false;
        }

        const tDate = new Date(transaction.date);
        return tDate.getMonth() === month && tDate.getFullYear() === year;
      });

      const income = monthTransactions
        .filter((transaction) => transaction.amount > 0)
        .reduce((sum, transaction) => sum + transaction.amount, 0);

      const expenses = Math.abs(
        monthTransactions
          .filter((transaction) => transaction.amount < 0)
          .reduce((sum, transaction) => sum + transaction.amount, 0)
      );

      result.push({
        label: MONTH_NAMES[month],
        fullLabel: `${MONTH_NAMES_FULL[month]} ${year}`,
        income,
        expenses,
      });
    }

    return result;
  }, [transactions, offsetMonths]);

  const maxValue = useMemo(() => {
    const allValues = months.flatMap((item) => [item.income, item.expenses]);
    const max = Math.max(1000, ...allValues);
    return Math.ceil(max / 1000) * 1000;
  }, [months]);

  const animatedData = months.map((item) => ({
    ...item,
    income: (item.income * animationProgress) / 100,
    expenses: (item.expenses * animationProgress) / 100,
  }));

  const legendColor = isDark ? '#f1f5f9' : '#0f172a';
  const tooltipBackground = isDark ? '#0f172a' : '#ffffff';
  const tooltipTitleColor = isDark ? '#f1f5f9' : '#0f172a';
  const tooltipBodyColor = isDark ? '#f1f5f9' : '#047857';
  const tooltipBorderColor = isDark ? '#334155' : '#5eead4';
  const gridColor = isDark ? '#1e293b' : '#d1fae5';
  const tickColor = isDark ? '#64748b' : '#0f172a';

  const data = {
    labels: animatedData.map((item) => item.label),
    datasets: [
      {
        label: 'Receitas',
        data: animatedData.map((item) => item.income),
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.92)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 0.55)');
          return gradient;
        },
        borderColor: '#10b981',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 40,
      },
      {
        label: 'Saídas',
        data: animatedData.map((item) => item.expenses),
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.9)');
          gradient.addColorStop(1, 'rgba(239, 68, 68, 0.55)');
          return gradient;
        },
        borderColor: '#ef4444',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 40,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: legendColor,
          font: {
            size: 12,
            weight: 600,
          },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: tooltipBackground,
        titleColor: tooltipTitleColor,
        bodyColor: tooltipBodyColor,
        borderColor: tooltipBorderColor,
        borderWidth: 2,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 13,
          weight: 600,
        },
        bodyFont: {
          size: 14,
          weight: 600,
        },
        callbacks: {
          title: function (context: any) {
            const index = context[0].dataIndex;
            return months[index]?.fullLabel ?? '';
          },
          label: function (context: any) {
            const datasetLabel = context.dataset.label || '';
            const base = months[context.dataIndex];
            const value = datasetLabel === 'Receitas' ? base?.income ?? 0 : base?.expenses ?? 0;
            return `${datasetLabel}: R$ ${value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          font: {
            size: 11,
            weight: 600,
          },
        },
      },
      y: {
        max: maxValue,
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          font: {
            size: 11,
            weight: 500,
          },
          callback: function (value: number | string) {
            const numericValue = typeof value === 'string' ? Number(value) : value;
            return `R$ ${(numericValue / 1000).toFixed(0)}k`;
          },
        },
      },
    },
  };

  const navigatePeriod = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setOffsetMonths((prev) => prev + 1);
    } else {
      setOffsetMonths((prev) => Math.max(0, prev - 1));
    }
  };

  const resetToCurrentPeriod = () => {
    setOffsetMonths(0);
  };

  const resolvedTitle = useMemo(() => title.replace('(6 meses)', '(trimestre)'), [title]);

  const rangeLabel = useMemo(() => {
    if (offsetMonths === 0) {
      return null;
    }

    const first = months[2];
    const last = months[0];

    if (!first || !last) {
      return null;
    }

    const firstLabel = first.fullLabel.split(' ')[0];
    return `${firstLabel} – ${last.fullLabel}`;
  }, [months, offsetMonths]);

  const containerClass = `rounded-xl p-6 h-64 relative overflow-hidden border transition-colors ${
    isDark
      ? 'bg-slate-900/95 border-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.35)] backdrop-blur-sm'
      : 'bg-white border-teal-200 shadow-[0_18px_45px_rgba(45,212,191,0.18)]'
  }`;

  const overlayClass = isDark
    ? 'bg-gradient-to-br from-emerald-500 via-transparent to-rose-500'
    : 'bg-gradient-to-br from-teal-200 via-transparent to-rose-200';

  const navButtonClass = `p-1.5 rounded-lg transition-all duration-200 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 ${
    isDark
      ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700'
      : 'bg-white border border-teal-200 text-teal-600 hover:bg-teal-50'
  }`;

  const resetButtonClass = `px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105 ${
    isDark
      ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-[0_16px_40px_rgba(16,185,129,0.35)]'
      : 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-500 hover:to-emerald-600 text-white shadow-[0_16px_40px_rgba(45,212,191,0.3)]'
  }`;

  return (
    <div className={containerClass}>
      <div className="absolute inset-0 opacity-5">
        <div className={`absolute inset-0 ${overlayClass}`} />
      </div>

      <div className="relative z-10 h-full flex flex-col">
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {resolvedTitle}
            {rangeLabel && (
              <span className={`text-xs font-medium ${isDark ? 'text-emerald-300' : 'text-teal-600'}`}>
                {rangeLabel}
              </span>
            )}
          </h3>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigatePeriod('prev')}
              className={navButtonClass}
              title="Ver trimestre anterior"
            >
              <ChevronLeft size={18} />
            </button>

            {offsetMonths > 0 && (
              <button type="button" onClick={resetToCurrentPeriod} className={resetButtonClass}>
                Atual
              </button>
            )}

            <button
              type="button"
              onClick={() => navigatePeriod('next')}
              className={navButtonClass}
              title="Avançar trimestre"
              disabled={offsetMonths === 0}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1">
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default BarChart;